import { ReactNode, lazy, Suspense, ComponentProps } from 'react';
import { useSelector, TypedUseSelectorHook, useDispatch } from 'react-redux';
import {
  Theme,
  AlertMessage,
  AccountType,
  Notification,
  AlertMessageWidth,
  IconAlignment,
  type NotificationPropType,
  type NotificationTheme,
  type Session,
} from '@nextgen-web-framework/all';
import {
  formatPrice,
  formatDateTime,
  DateFormats,
  TimeFormats,
  DateOrTime,
  type AppState,
  type AppDispatch,
  ConfigService,
  formatDate,
} from '@nextgen-web-framework/core';
import {
  type AmwayUser,
  SubscriptionResponse,
  InitialSubscriptionState,
  SubscriptionResponseStatusEnum,
  SubscriptionType,
  ElementStatus,
  nullishOperator,
} from '@commerce-subscription-webcomponents/sdk';
import { Cart, SubCart } from '@commerce-cart-webcomponents/sdk';
import { productDataTransformer } from '@commerce-product-webcomponents/sdk';
import { subscriptionStepsList } from '../components/common/constant';

export const getNumberWithOrdinal = (n: number, t: AppState, local?: string[] | string) => {
  const s = [
      t('subscription.numberOrdinal.th'),
      t('subscription.numberOrdinal.st'),
      t('subscription.numberOrdinal.nd'),
      t('subscription.numberOrdinal.rd'),
    ],
    v = n % 100;
  const ordinal = local == 'th' ? '' : s[(v - 20) % 10] || s[v] || s[0];
  return `${n}${ordinal}`;
};

export const getSubscriptionPaymentDateText = (
  date: number,
  subscriptionDetails: SubscriptionResponse,
  t: AppState,
  local?: string[] | string,
) => {
  const { frequency } = subscriptionDetails?.plan?.cycle || { frequency: 1 };
  const ordinalDate = getNumberWithOrdinal(date, t, local);

  let frequencyText: string;
  switch (frequency) {
    case 1:
      frequencyText = t('subscription.paymentDate.drawer.ofEveryMonth.text', {
        date: ordinalDate,
      });
      break;
    case 2:
      frequencyText = t('subscription.paymentDate.drawer.biMonthly.text', {
        date: ordinalDate,
      });
      break;
    case 3:
      frequencyText = t('subscription.paymentDate.drawer.quaterly.text', {
        date: ordinalDate,
      });
      break;
    case 6:
      frequencyText = t('subscription.paymentDate.drawer.semiAnnually.text', {
        date: ordinalDate,
      });
      break;
    default:
      frequencyText = t('subscription.paymentDate.drawer.ofEveryMonth.text', {
        date: ordinalDate,
      });
  }

  return frequencyText;
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getFormattedDate = (date: number): string => {
  const dt = new Date();
  const month = dt.getDate() < date ? dt.getMonth() : dt.getMonth() + 1;
  return `${months[month == 12 ? 0 : month]} ${date}, ${dt.getFullYear()}`;
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function copyText(
  text: string,
  toastMsg: string | ReactNode,
  addToast: any,
  theme?: Theme.SUCCESS | Theme.WARNING | Theme.ERROR | Theme.ACTION | Theme.GRAY | Theme.PURPLE | Theme.BLACK,
) {
  navigator.clipboard.writeText(text);
  showNotification(toastMsg, addToast, theme);
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const showNotification = (
  msg: string | ReactNode,
  addToast: any,
  theme?: Theme.SUCCESS | Theme.WARNING | Theme.ERROR | Theme.ACTION | Theme.GRAY | Theme.PURPLE | Theme.BLACK,
) => {
  const them = nullishOperator(theme, Theme.SUCCESS);
  addToast({
    id: 'copy-text-toast',
    timeout: 3000,
    content: <AlertMessage theme={them}>{msg}</AlertMessage>,
  });
};

export const showToastMsg = ({
  id,
  addToast,
  deleteToast,
  message,
  description = '',
  theme,
  hasBoldText = true,
  hasNotificationIcon = true,
  contentType = 'Notification',
  textLink = { text: '', onClick: () => null },
}: {
  id: string;
  addToast: AppState;
  deleteToast?: AppState;
  message: string;
  description?: string;
  theme?: NotificationTheme;
  hasBoldText?: boolean;
  hasNotificationIcon?: boolean;
  contentType?: string;
  textLink?: { text: string; onClick: () => void };
}) => {
  const them = nullishOperator(theme, Theme.SUCCESS);
  const notificationProps: NotificationPropType = {
    message,
    description,
    theme: them,
    hasBoldText,
    hasNotificationIcon,
  };

  let content: React.JSX.Element;
  if (contentType === 'Notification') {
    content = <Notification {...notificationProps} onCloseClick={() => deleteToast?.(id)} />;
  } else {
    content = (
      <AlertMessage
        theme={them}
        textLink={{ text: textLink?.text, onClick: textLink?.onClick }}
        width={AlertMessageWidth.FULL_WIDTH}
        iconAlignment={IconAlignment.CENTER}
      >
        {message}
      </AlertMessage>
    );
  }

  addToast({
    id: id,
    timeout: 3000,
    content: content,
  });
};

export const amwayUserData = (session: Session): AmwayUser => ({
  accountId: nullishOperator(session?.aboNum, '') as string,
  accountType: session?.type as AccountType,
  userId: nullishOperator(session?.id, '') as string,
});
export const getFormattedPrice = (value?: number, curr?: string) => {
  if (!value) {
    return '';
  }
  return `${formatPrice(value, curr)}`;
};

export const getCurrentElementFromSubscription = (subscriptionDetails: SubscriptionResponse) =>
  subscriptionDetails?.currentElements?.find((element) => !element?.isCompensated);

// For checking sop items in cart
export const checkIfSubscriptionInCart = (subscriptionDetails: SubscriptionResponse, cartDetails: Cart) => {
  const currentElement = getCurrentElementFromSubscription(subscriptionDetails);

  return cartDetails?.subCarts?.some(
    (subCart: SubCart) =>
      subCart?.sopMetaData?.length &&
      subCart.sopMetaData?.some(
        (sopData) =>
          sopData?.sopElementId === currentElement?.id &&
          (sopData?.sopId === subscriptionDetails?.id || sopData?.sopCode === subscriptionDetails?.code),
      ),
  );
};

/**
 * Lazy load a component
 */
export const lazyLoad = <T extends React.ComponentType<AppState>>(
  importFn: () => Promise<{ default: T }>,
  fallback: React.ReactNode,
) => {
  const LazyComponent = lazy(importFn);

  // eslint-disable-next-line react/display-name
  return (props: ComponentProps<T>) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

export const getProductDetails = (productId: string, subscriptionDetails: SubscriptionResponse) =>
  subscriptionDetails.products?.find((p) => {
    const transformedProductData = productDataTransformer(p);
    return transformedProductData.productId === productId;
  });

// redux helpers
export const useSubscriptionsSelector: TypedUseSelectorHook<{
  commerceSubscription: InitialSubscriptionState;
}> = useSelector;
export const useSubscriptionsDispatch = useDispatch<AppDispatch>;

export const getSubscriptionsDisabledClass = (subscriptionDetails: SubscriptionResponse) =>
  (subscriptionDetails && subscriptionDetails?.status === SubscriptionResponseStatusEnum.Cancelled) ||
  subscriptionDetails?.status === SubscriptionResponseStatusEnum.Paused
    ? 'disable'
    : '';

export function dateTimeFormatter(
  date?: string,
  dateFormat?: DateFormats,
  timeFormat?: TimeFormats,
  dateOrTime?: DateOrTime,
): string {
  const configs = ConfigService.getConfigs();

  if (!date) {
    return '';
  }
  return formatDateTime(
    date,
    { date: dateFormat, time: timeFormat },
    false,
    configs.NEXT_PUBLIC_TIMEZONE as string,
    undefined,
    dateOrTime,
  );
}

export const shouldShowEditOrder = (subscriptionDetails: SubscriptionResponse) => {
  const currentElement = getCurrentElementFromSubscription(subscriptionDetails);
  let showEditOrder = true;
  if (
    subscriptionDetails?.type === SubscriptionType.Manual &&
    currentElement &&
    currentElement?.status === ElementStatus.Completed
  ) {
    showEditOrder = false;
  }
  return showEditOrder;
};

type Procedure = (...args: any[]) => void;

class DebounceManager<T extends Procedure> {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private fn: T | null = null;
  private delay = 300;

  setFunction(fn: T, delay = 300) {
    this.fn = fn;
    this.delay = delay;
  }

  call(...args: Parameters<T>) {
    if (!this.fn) {
      return;
    }
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => this.fn?.(...args), this.delay);
  }

  cancel() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = null;
  }

  getTimer() {
    return this.timer;
  }
}

export const debounceManager = new DebounceManager<(...args: any[]) => void>();

export const getDefaultSopStepsList = (enablePlanDetailsStepsAndBenefitsRedesign = false) => {
  if (enablePlanDetailsStepsAndBenefitsRedesign) {
    return subscriptionStepsList.map((step) => ({
      ...step,
      isAccordianOpened: true,
    }));
  }
  return subscriptionStepsList;
};

export const getDatePeriod = (dateString: string, dateFormat: DateFormats) => {
  const locale = PreferenceService.getPreferences()?.locale || 'en-us';
  const date = formatDate(dateString, dateFormat, locale);
  const isThaiLocale = locale.trim().toLowerCase().startsWith('th');
  // eslint-disable-next-line prefer-const
  let [month, endDay, year] = date.split(' ');
  if (isThaiLocale) {
    [month, endDay] = [endDay, month];
  }
  return `${month} 1–${endDay} ${year}`;
};
